import { useDocumentRouteReady } from "../components/RouteCanvas";
import { RecipientGroups } from "./RecipientGroups";
import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";

import { useSession } from "../auth/session";
import { Button } from "../components/Button";
import { ContextualPopover } from "../components/ContextualPopover";
import { Notice, Skeleton } from "../components/Feedback";
import { RequestNotice } from "../components/RequestNotice";
import { SegmentedControl } from "../components/FormControls";
import { RegisterStatusChip, RegisterTable } from "../experience/RegisterPrimitives";
import { navigate } from "../routing/useLocationPath";
import { clientRequest, type BillingAddress, type ClientRecord, type ContactRecord, type RecordState } from "./clientApi";
import { ContactFormModal } from "./ClientForms";

type DetailLoadState = "loading" | "ready" | "refresh-error" | "blocking-error";

export function ClientDetailPage({ clientId }: { clientId: string }) {
  const { getToken, sessionId, state: sessionState } = useSession();
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;
  const addContactRef = useRef<HTMLButtonElement>(null);
  const contactActionRef = useRef<HTMLButtonElement>(null);
  const [client, setClient] = useState<ClientRecord | null>(null);
  const clientRef = useRef(client);
  clientRef.current = client;
  const [contacts, setContacts] = useState<ContactRecord[]>([]);
  const [contactScope, setContactScope] = useState<RecordState>("active");
  const [loadState, setLoadState] = useState<DetailLoadState>("loading");
  useDocumentRouteReady(loadState !== "loading");
  const [retryKey, setRetryKey] = useState(0);
  const [contactOpen, setContactOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactRecord | null>(null);
  const [actionError, setActionError] = useState("");
  const [actionStatus, setActionStatus] = useState("");
  const [primarySaving, setPrimarySaving] = useState(false);
  const menuAnchor = useRef<HTMLButtonElement | null>(null);
  const [menu, setMenu] = useState<ContactRecord | "client" | null>(null);
  function closeMenu() { setMenu(null); menuAnchor.current?.focus({ preventScroll: true }); }

  const load = useCallback(async (signal: AbortSignal) => {
    setLoadState((current) => current === "ready" || current === "refresh-error" ? current : "loading");
    try {
      const [nextClient, nextContacts] = await Promise.all([
        clientRequest<ClientRecord>(getTokenRef.current, `/api/clients/${clientId}`, { signal }),
        clientRequest<{ contacts: ContactRecord[] }>(getTokenRef.current, `/api/clients/${clientId}/contacts?state=${contactScope}`, { signal }),
      ]);
      if (signal.aborted) return;
      setClient(nextClient);
      setContacts(nextContacts.contacts);
      setLoadState("ready");
      setActionError("");
    } catch {
      if (!signal.aborted) setLoadState(clientRef.current ? "refresh-error" : "blocking-error");
    }
  }, [clientId, contactScope]);

  useEffect(() => {
    if (sessionState !== "signed-in" || !sessionId) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, retryKey, sessionId, sessionState]);

  function backToClients(event: MouseEvent<HTMLAnchorElement>) {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate("/clients");
  }

  async function changeClientState() {
    if (!client) return;
    const action = client.state === "active" ? "archive" : "restore";
    setActionError("");
    try {
      const updated = await clientRequest<ClientRecord>(getTokenRef.current, `/api/clients/${client.id}/${action}`, {
        method: "POST",
        body: JSON.stringify({ version: client.version }),
      });
      setClient((current) => current ? { ...updated, primaryContact: current.primaryContact } : updated);
      setRetryKey((value) => value + 1);
    } catch (failure) {
      setActionError(failure instanceof Error ? failure.message : "The client state could not be changed.");
    }
  }

  async function saveContact(input: { name: string; email: string | null; phone: string | null; isPrimary: boolean; billingAddress: BillingAddress }) {
    if (!client) return;
    const path = editingContact
      ? `/api/clients/${client.id}/contacts/${editingContact.id}`
      : `/api/clients/${client.id}/contacts`;
    await clientRequest<ContactRecord>(getTokenRef.current, path, {
      method: editingContact ? "PATCH" : "POST",
      body: JSON.stringify(editingContact ? { ...input, version: editingContact.version } : input),
    });
    setContactOpen(false);
    setEditingContact(null);
    setContactScope("active");
    setRetryKey((value) => value + 1);
  }

  async function changeContactState(contact: ContactRecord) {
    if (!client) return;
    const action = contact.state === "active" ? "archive" : "restore";
    setActionError("");
    try {
      await clientRequest<ContactRecord>(getTokenRef.current, `/api/clients/${client.id}/contacts/${contact.id}/${action}`, {
        method: "POST",
        body: JSON.stringify({ version: contact.version }),
      });
      setRetryKey((value) => value + 1);
      addContactRef.current?.focus({ preventScroll: true });
    } catch (failure) {
      setActionError(failure instanceof Error ? failure.message : "The contact state could not be changed.");
    }
  }

  async function makePrimary(contact: ContactRecord) {
    if (!client || client.state !== "active" || contact.state !== "active" || contact.isPrimary || primarySaving) return;
    setPrimarySaving(true);
    setActionError("");
    setActionStatus("");
    try {
      const updated = await clientRequest<ContactRecord>(getTokenRef.current, `/api/clients/${client.id}/contacts/${contact.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: contact.name, email: contact.email, phone: contact.phone, isPrimary: true, version: contact.version }),
      });
      setClient(current => current ? { ...current, primaryContact: updated } : current);
      setContacts(current => current.map(item => item.id === updated.id ? updated : item.isPrimary ? { ...item, isPrimary: false, version: item.version + 1 } : item));
      setActionStatus(`${updated.name || updated.email} is now the primary contact.`);
    } catch (failure) {
      setActionError(failure instanceof Error ? failure.message : "The primary contact could not be changed.");
    } finally {
      setPrimarySaving(false);
    }
  }

  if (loadState === "blocking-error") {
    return <div className="client-detail-page"><a className="back-link" href="/clients" onClick={backToClients}>← Clients</a><Notice title="Client unavailable" tone="danger">This record could not be loaded. <Button variant="secondary" onClick={() => setRetryKey((value) => value + 1)}>Retry</Button></Notice></div>;
  }

  if (!client) {
    return <div className="client-detail-page" aria-label="Loading client"><Skeleton height={24} width="32%" /><Skeleton height={84} /><Skeleton height={240} /></div>;
  }

  const primary = client.primaryContact;
  const contactIdentity = (contact: ContactRecord) => <span className="client-record-identity"><span className="sbx-record-link"><strong>{contact.name || contact.email}</strong></span>{contact.isPrimary ? <RegisterStatusChip status="Primary" /> : null}</span>;
  const contactDetails = (contact: ContactRecord) => <span className="client-primary-contact">
    <span className="client-contact-name">{contact.email || contact.phone || "No email or phone recorded"}</span>
    {contact.email && contact.phone ? <span className="sbx-cell-secondary">{contact.phone}</span> : null}
    {!contact.email ? <span className="sbx-cell-secondary">Add an email address before using this contact for document delivery.</span> : null}
    {contact.billingAddress && Object.values(contact.billingAddress).some(Boolean) ? <span className="sbx-cell-secondary">{[contact.billingAddress.line1, contact.billingAddress.line2, contact.billingAddress.locality, contact.billingAddress.region, contact.billingAddress.postalCode, contact.billingAddress.country].filter(Boolean).join(", ")}</span> : null}
  </span>;
  const contactActions = (contact: ContactRecord) => <Button className="sbx-register-more" variant="quiet" aria-label={`Actions for ${contact.name || contact.email}`} aria-haspopup="menu" aria-expanded={menu === contact} disabled={primarySaving || client.state !== "active"} onClick={event => { menuAnchor.current = event.currentTarget; setMenu(menu === contact ? null : contact); }}><svg aria-hidden="true" viewBox="0 0 16 16" fill="currentColor"><circle cx="3.5" cy="8" r=".9"/><circle cx="8" cy="8" r=".9"/><circle cx="12.5" cy="8" r=".9"/></svg></Button>;
  return (
    <div className="client-detail-page">
      <a className="back-link" href="/clients" onClick={backToClients}>← Clients</a>
      <div className="client-record-header">
        <header className="sbx-register-header">
          <div>
            <p>Client record</p>
            <div className="client-record-identity"><h1>{client.displayName}</h1><RegisterStatusChip status={client.state === "active" ? "Active" : "Archived"} /></div>
            <section className="primary-contact" aria-labelledby="primary-contact-title">
              <p className="eyebrow">Primary contact</p>
              <h2 id="primary-contact-title">{primary ? primary.name || primary.email : "No primary contact"}</h2>
              {primary ? <p>{[primary.email, primary.phone].filter(Boolean).join(" · ") || "No email or phone recorded"}</p> : <p>Choose one active contact as primary when the relationship requires it.</p>}
              {primary && !primary.email ? <p className="sbx-cell-secondary">Add an email address before using this contact for document delivery.</p> : null}
            </section>
          </div>
          <div className="record-actions">
            <Button variant="primary" disabled={client.state !== "active"} onClick={() => navigate(`/clients/${client.id}/edit`)}>Edit client</Button>
            <Button className="sbx-register-more" variant="quiet" aria-label={`Actions for ${client.displayName}`} aria-haspopup="menu" aria-expanded={menu === "client"} onClick={event => { menuAnchor.current = event.currentTarget; setMenu(menu === "client" ? null : "client"); }}><svg aria-hidden="true" viewBox="0 0 16 16" fill="currentColor"><circle cx="3.5" cy="8" r=".9"/><circle cx="8" cy="8" r=".9"/><circle cx="12.5" cy="8" r=".9"/></svg></Button>
          </div>
        </header>
      </div>

      {loadState === "refresh-error" ? <Notice title="Client could not refresh" tone="caution">The current record is retained. <Button variant="quiet" onClick={() => setRetryKey((value) => value + 1)}>Retry</Button></Notice> : null}
      {actionError ? <RequestNotice title="Change not saved" message={actionError} /> : null}
      {actionStatus ? <p role="status">{actionStatus}</p> : null}

      <section className="contacts-section" aria-labelledby="contacts-title">
        <header className="sbx-register-section">
          <div><h2 id="contacts-title">Contacts</h2><p>People associated with this client. Choose a primary contact for the relationship.</p></div>
          <div className="sbx-register-tools">
            <SegmentedControl className="sbx-scope" label="Contact state" value={contactScope} onChange={(value) => { setMenu(null); setContactScope(value); setContacts([]); setLoadState("loading"); }} options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }]} />
            <Button ref={addContactRef} disabled={client.state !== "active"} onClick={() => { setEditingContact(null); setContactOpen(true); }}>Add contact</Button>
          </div>
        </header>
        <div className="client-detail-desktop">
          <RegisterTable label="Contacts" columns={[{ key: "contact", label: "Contact" }, { key: "details", label: "Email or phone" }, { key: "actions", label: "Actions", align: "right" }]} selectable={false} allSelected={false} partiallySelected={false} onSelectAll={() => {}} loading={loadState === "loading" && !contacts.length} empty={contactScope === "active" ? "No contacts yet. Add a contact when someone represents this client." : "No archived contacts. Archived contacts remain retained and restorable."} rows={contacts.length ? contacts.map(contact => <tr key={contact.id} className="client-contact-record"><td>{contactIdentity(contact)}</td><td>{contactDetails(contact)}</td><td className="sbx-align-right">{contactActions(contact)}</td></tr>) : null} />
        </div>
        <div className="client-list client-detail-compact" role="list" aria-label="Contacts">
          {contacts.map(contact => <article key={contact.id} className="client-row client-contact-record" role="listitem"><div className="client-row-link"><span className="client-identity">{contactIdentity(contact)}</span>{contactDetails(contact)}</div>{contactActions(contact)}</article>)}
          {!contacts.length ? <p className="sbx-cell-secondary">{loadState === "loading" ? "Loading contacts…" : contactScope === "active" ? "No contacts yet. Add a contact when someone represents this client." : "No archived contacts. Archived contacts remain retained and restorable."}</p> : null}
        </div>
      </section>

      <RecipientGroups key={client.id} clientId={client.id} disabled={client.state !== "active"} />
      {menu ? <ContextualPopover anchorRef={menuAnchor} width={220} title={menu === "client" ? client.displayName : menu.name || menu.email} label={`Actions for ${menu === "client" ? client.displayName : menu.name || menu.email}`} className="sbx-sort-popover" focusFirst onClose={() => setMenu(null)}>
        <div role="menu" aria-label={`Actions for ${menu === "client" ? client.displayName : menu.name || menu.email}`} onKeyDown={event => {
          if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
          event.preventDefault();
          const controls = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
          const index = controls.indexOf(document.activeElement as HTMLButtonElement);
          controls[event.key === "Home" ? 0 : event.key === "End" ? controls.length - 1 : (index + (event.key === "ArrowDown" ? 1 : -1) + controls.length) % controls.length]?.focus();
        }}>
          {menu === "client" ? <button type="button" role="menuitem" onClick={() => { closeMenu(); void changeClientState(); }}>{client.state === "active" ? "Archive client" : "Restore client"}</button> : <>
            {!menu.isPrimary && menu.state === "active" ? <button type="button" role="menuitem" onClick={() => { closeMenu(); void makePrimary(menu); }}>Make primary</button> : null}
            <button type="button" role="menuitem" disabled={menu.state !== "active"} onClick={() => { contactActionRef.current = menuAnchor.current; setEditingContact(menu); setContactOpen(true); setMenu(null); }}>Edit</button>
            <button type="button" role="menuitem" onClick={() => { closeMenu(); void changeContactState(menu); }}>{menu.state === "active" ? "Archive" : "Restore"}</button>
          </>}
        </div>
      </ContextualPopover> : null}

      <ContactFormModal key={editingContact ? `${editingContact.id}-${editingContact.version}` : "new-contact"} open={contactOpen} label={editingContact ? "Edit contact" : "Add contact"} contact={editingContact ?? undefined} onClose={() => { setContactOpen(false); setEditingContact(null); }} onSubmit={saveContact} restoreFocusRef={editingContact ? contactActionRef : addContactRef} />
    </div>
  );
}
